import { TUserGroup } from '@answerr/types';
import { Check, UserRound, UsersRound } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Select, { GroupBase, MultiValue, OptionProps, components } from 'react-select';
import EnterpriseUserService from '../../services/enterprise-data.service';
import { inputLabelStyles } from '../../global-styles';
import { useAppSelector } from '../../hooks/redux-hooks';
import UserGroupService from '../../services/user-group.service';
import { ExtOrgUser } from '../../types/organisation.types';

type AssignmentOption = {
  value: string;
  label: string;
  subtitle: string;
  type: 'group' | 'user';
};

const AssignmentOptionRow = (props: OptionProps<AssignmentOption, true>) => {
  const { data, isSelected } = props;

  return (
    <components.Option {...props}>
      <div className={`flex items-center justify-between px-4 py-2 ${isSelected ? 'bg-[#DBEAFE]' : ''}`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-9 w-9 min-w-9 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#475569]">
            {data.type === 'group' ? <UsersRound size={18} /> : <UserRound size={18} />}
          </span>

          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-[#020817] truncate">{data.label}</p>
            <p className="text-[12px] text-[#64748B] truncate">{data.subtitle}</p>
          </div>
        </div>

        {isSelected ? (
          <span className="h-8 w-8 rounded-full bg-[#2563EB] text-white flex items-center justify-center min-w-8">
            <Check size={16} />
          </span>
        ) : null}
      </div>
    </components.Option>
  );
};

const ManageUsersandGroups: FC<{
  onUserSelect: (ids: string[]) => void;
  onGroupSelect: (ids: string[]) => void;
  selectedUserIds: string[];
  selectedGroupIds?: string[];
  selectedeGroupIds?: string[];
  menuPlacement?: 'top' | 'bottom';
}> = ({ onUserSelect, onGroupSelect, selectedUserIds, selectedGroupIds, selectedeGroupIds, menuPlacement }) => {
  const { orgInfo } = useAppSelector((state) => state.common);
  const [users, setUsers] = useState<ExtOrgUser[]>([]);
  const [groups, setGroups] = useState<TUserGroup[]>([]);

  const effectiveSelectedGroupIds = useMemo(() => selectedGroupIds || selectedeGroupIds || [], [selectedGroupIds, selectedeGroupIds]);

  const getOrgUsers = useCallback(async () => {
    if (!orgInfo?._id) return;
    try {
      const response = await EnterpriseUserService.getOrgUserList();
      if (response.data.data?.users && response.data.data.users.length > 0) {
        setUsers(response.data.data.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching organization users:', error);
    }
  }, [orgInfo?._id]);

  const getOrgGroups = useCallback(async () => {
    try {
      const response = await UserGroupService.getAllUserGroups();
      if (response.data.data?.userGroups && response.data.data.userGroups.length > 0) {
        setGroups(response.data.data.userGroups);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error('Error fetching user groups:', error);
    }
  }, []);

  const usersOptions = useMemo<AssignmentOption[]>(() => {
    return users.map((user) => {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();

      return {
        value: user._id,
        label: user.email,
        subtitle: fullName || user.email,
        type: 'user',
      };
    });
  }, [users]);

  const groupOptions = useMemo<AssignmentOption[]>(() => {
    return groups
      .filter((group): group is TUserGroup & { _id: string } => Boolean(group._id))
      .map((group) => ({
        value: group._id,
        label: group.groupName,
        subtitle: `${group.userIds?.length || 0} members`,
        type: 'group',
      }));
  }, [groups]);

  const options = useMemo<GroupBase<AssignmentOption>[]>(() => {
    return [
      { label: 'GROUPS', options: groupOptions },
      { label: 'USERS', options: usersOptions },
    ].filter((group) => group.options.length > 0);
  }, [groupOptions, usersOptions]);

  const selectedOptions = useMemo(() => {
    const selectedUsers = usersOptions.filter((option) => selectedUserIds.includes(option.value));
    const selectedGroups = groupOptions.filter((option) => effectiveSelectedGroupIds.includes(option.value));

    return [...selectedGroups, ...selectedUsers];
  }, [usersOptions, groupOptions, selectedUserIds, effectiveSelectedGroupIds]);

  const handleSelection = (selectedOptionsList: MultiValue<AssignmentOption>) => {
    const groupIds: string[] = [];
    const userIds: string[] = [];

    selectedOptionsList.forEach((option) => {
      if (option.type === 'group') {
        groupIds.push(option.value);
      } else {
        userIds.push(option.value);
      }
    });

    onGroupSelect(groupIds);
    onUserSelect(userIds);
  };

  useEffect(() => {
    getOrgUsers();
    getOrgGroups();
  }, [getOrgUsers, getOrgGroups]);

  return (
    <div>
      <p className={inputLabelStyles}>Assign Users</p>
      <Select<AssignmentOption, true, GroupBase<AssignmentOption>>
        options={options}
        value={selectedOptions}
        isMulti={true}
        menuPlacement={menuPlacement}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        components={{ Option: AssignmentOptionRow }}
        maxMenuHeight={420}
        onChange={handleSelection}
        styles={{
          control: (provided, state) => ({
            ...provided,
            borderRadius: '6px',
            background: 'transparent',
            borderColor: state.isFocused ? '#020817' : '#E2E8F0',
            boxShadow: state.isFocused ? 'none' : 'none',
            '&:hover': { borderColor: '#020817' },
          }),
          menu: (provided) => ({
            ...provided,
            borderRadius: '8px',
            border: '1px solid #E2E8F0',
            boxShadow: '0 10px 10px rgba(2, 8, 23, 0.08)',
            overflow: 'hidden',
          }),
          menuList: (provided) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
          }),
          groupHeading: (provided) => ({
            ...provided,
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 700,
            color: '#64748B',
            padding: '8px',
            margin: 0,
          }),
          option: (provided) => ({
            ...provided,
            padding: 0,
            backgroundColor: 'transparent',
            color: '#020817',
          }),
          multiValue: (provided) => ({
            ...provided,
            borderRadius: '8px',
            background: '#E2E8F0',
            padding: '2px 4px',
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            fontSize: '14px',
            color: '#0F172A',
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            color: '#0F172A',
            ':hover': {
              backgroundColor: '#CBD5E1',
              color: '#020817',
            },
          }),
        }}
      />
    </div>
  );
};

export default ManageUsersandGroups;
